// Pointregler for Es-makker Whist. Port af docs/prototype/index.html
// (LEVELS, VARIANTS, SOLO, bidScore, sittingOut, dealer, distribute) samt
// bidType-konvertering fra den gamle GamePage.
import type { BidType, TrumpSuit } from '../../../shared/types'

export type Level = 7 | 8 | 9 | 10 | 11 | 12 | 13
export type Variant = 'Almindelige' | 'Halve' | 'Gode' | '1. Vip' | '2. Vip' | '3. Vip'
export type Solo = 'Sol' | 'Ren sol' | 'Ren bordlægger' | 'Dobbelt bordlægger'

export const LEVELS: Level[] = [7, 8, 9, 10, 11, 12, 13]
export const VARIANTS: Variant[] = ['Almindelige', 'Halve', 'Gode', '1. Vip', '2. Vip', '3. Vip']
export const SOLO: Solo[] = ['Sol', 'Ren sol', 'Ren bordlægger', 'Dobbelt bordlægger']
export const SUITS: Array<[TrumpSuit, string, 'k' | 'r']> = [
  ['Klør', '♣', 'k'],
  ['Ruder', '♦', 'r'],
  ['Hjerter', '♥', 'r'],
  ['Spar', '♠', 'k'],
  ['Trumfløs', 'Trumfløs', 'k']
]

const SOLO_VALUE: Record<Solo, number> = {
  Sol: 24,
  'Ren sol': 48,
  'Ren bordlægger': 192,
  'Dobbelt bordlægger': 384
}
const BASE: Record<Level, number> = { 7: 1, 8: 2, 9: 4, 10: 8, 11: 16, 12: 32, 13: 64 }
const MULT: Record<Variant, number> = {
  Almindelige: 1,
  Halve: 2,
  Gode: 2,
  '1. Vip': 2,
  '2. Vip': 4,
  '3. Vip': 8
}

export interface Player {
  userId: number
  name: string
}

/** Runde-input til bidScore/distribute. Solo-runder sætter kun solo+won. */
export interface RoundInput {
  bidderId: number
  partnerId?: number
  level?: Level
  variant?: Variant
  tricks?: number
  solo?: Solo
  won?: boolean
  trump?: TrumpSuit
}

/** Varianter hvor klør som trumf fordobler. Gode spilles altid i klør. */
export function doublesInClubs(variant: Variant): boolean {
  return variant === 'Halve' || variant.includes('Vip')
}

export function bidScore(r: RoundInput): number {
  if (r.solo) return r.won ? SOLO_VALUE[r.solo] : -SOLO_VALUE[r.solo]
  const level = r.level as Level
  const variant = r.variant as Variant
  const clubsFactor = r.trump === 'Klør' && doublesInClubs(variant) ? 2 : 1
  const V = BASE[level] * MULT[variant] * clubsFactor
  const d = (r.tricks as number) - level
  if (d >= 0) return V * (d + 1)
  return variant === 'Almindelige' ? V * (d - 1) : V * d
}

/** userId'er der sidder over i runden med dette (0-indekserede) roundIndex. */
export function sittingOut(players: Player[], roundIndex: number): number[] {
  const n = players.length
  if (n <= 4) return []
  const out: number[] = []
  for (let i = 0; i < n - 4; i++) out.push(players[(roundIndex + i) % n].userId)
  return out
}

export function dealer(players: Player[], roundIndex: number): Player {
  return players[roundIndex % players.length]
}

/** Point pr. spiller (userId) for én runde. */
export function distribute(players: Player[], roundIndex: number, r: RoundInput): Record<number, number> {
  const s = bidScore(r)
  const out = sittingOut(players, roundIndex)
  const res: Record<number, number> = {}
  for (const p of players) res[p.userId] = 0
  for (const p of players) {
    if (out.includes(p.userId)) continue
    if (r.solo) {
      res[p.userId] = p.userId === r.bidderId ? s : Math.round(-s / 3)
    } else if (r.partnerId === r.bidderId) {
      res[p.userId] = p.userId === r.bidderId ? s * 3 : -s
    } else {
      res[p.userId] = p.userId === r.bidderId || p.userId === r.partnerId ? s : -s
    }
  }
  return res
}

/** Bygger API-strengen for en melding, fx "9 Gode" eller "Sol". */
export function toBidType({ level, variant, solo }: { level?: Level; variant?: Variant; solo?: Solo }): BidType {
  if (solo) return solo as BidType
  return `${level} ${variant}` as BidType
}

/** Modsat af toBidType, til at forudfylde panelet ved redigering. */
export function fromBidType(bidType: BidType): { level?: Level; variant?: Variant; solo?: Solo } {
  if ((SOLO as string[]).includes(bidType)) return { solo: bidType as Solo }
  const match = String(bidType).match(/^(\d+)\s+(.+)$/)
  if (!match) return {}
  return { level: Number(match[1]) as Level, variant: match[2] as Variant }
}

/** Konverterer en runde fra API'et til RoundInput, til brug i historik/preview. */
export function inputFromRound(round: {
  bidderId: number
  partnerId?: number
  bidType: BidType
  tricksTaken: number
  won?: boolean
  trumpSuit?: TrumpSuit
}): RoundInput {
  const { level, variant, solo } = fromBidType(round.bidType)
  return {
    bidderId: round.bidderId,
    partnerId: round.partnerId,
    level,
    variant,
    solo,
    tricks: round.tricksTaken,
    won: round.won,
    trump: variant === 'Gode' ? 'Klør' : round.trumpSuit
  }
}
